import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';
import { useAIAuthHeaders } from '@/features/ai-agents/shared/use-ai-auth-headers';
import { incrementPinsTabBadgeCount } from '@/features/dashboard/pins/pins-tab-badge';

export function usePinsApi(isLocalHost = false) {
  const { headers } = useAIAuthHeaders(isLocalHost);

  const { get, post, put, patch, del, loading } = useFetch(
    ``,
    (globalOptions) => ({
      ...globalOptions,
      ...{
        headers,
        credentials: `include`,
        cachePolicy: CachePolicies.NO_CACHE,
        loading: false,
      },
    }),
  );

  const createPin = async (
    agentId: string,
    sessionId: string,
    messageId: string,
  ): Promise<PinnedReport> => {
    const pin = await post(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiCreatePin(agentId, sessionId))
        : routes.aiCreatePin(agentId, sessionId),
      { message_id: messageId },
    );
    incrementPinsTabBadgeCount();
    return pin;
  };

  const replacePin = async (
    agentId: string,
    sessionId: string,
    pinId: string,
    messageId: string,
  ): Promise<PinnedReport> =>
    put(
      isLocalHost
        ? routes.convertToLocalAiUrl(
            routes.aiReplacePin(agentId, sessionId, pinId),
          )
        : routes.aiReplacePin(agentId, sessionId, pinId),
      { message_id: messageId },
    );

  const listPins = async (
    limit: number,
    offset: number,
    search?: string,
    labels?: PinLabel[],
  ): Promise<PinnedReportListResponse> =>
    get(
      isLocalHost
        ? routes.convertToLocalAiUrl(
            routes.aiListPins(limit, offset, search, labels),
          )
        : routes.aiListPins(limit, offset, search, labels),
    );

  const getPin = async (pinId: string): Promise<PinnedReport> =>
    get(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiGetPin(pinId))
        : routes.aiGetPin(pinId),
    );

  const runPin = async (
    pinId: string,
    startDate: string,
    endDate: string,
  ): Promise<RunPinResponse> =>
    post(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiRunPin(pinId))
        : routes.aiRunPin(pinId),
      { start_date: startDate, end_date: endDate },
    );

  const updatePin = async (
    pinId: string,
    body: UpdatePinRequest,
  ): Promise<PinnedReport> =>
    patch(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiUpdatePin(pinId))
        : routes.aiUpdatePin(pinId),
      body,
    );

  const deletePin = async (pinId: string): Promise<void> =>
    del(
      isLocalHost
        ? routes.convertToLocalAiUrl(routes.aiDeletePin(pinId))
        : routes.aiDeletePin(pinId),
    );

  const updateSectionView = async (
    pinId: string,
    sectionName: string,
    body: UpdateSectionViewRequest,
  ): Promise<UpdateSectionViewResponse> =>
    patch(
      isLocalHost
        ? routes.convertToLocalAiUrl(
            routes.aiUpdatePinSectionView(pinId, sectionName),
          )
        : routes.aiUpdatePinSectionView(pinId, sectionName),
      body,
    );

  return {
    createPin,
    replacePin,
    listPins,
    getPin,
    runPin,
    updatePin,
    updateSectionView,
    deletePin,
    loading,
  };
}

export const PIN_LABELS = [
  `Sales & Revenue`,
  `Orders`,
  `Customers`,
  `Products & Catalog`,
  `Marketing & Campaigns`,
  `Traffic & Acquisition`,
  `Conversion & Funnel`,
  `Retention & Loyalty`,
  `Discounts & Coupons`,
  `Inventory & Fulfillment`,
] as const;

export type PinLabel = typeof PIN_LABELS[number];

export interface PinLabelFacet {
  label: PinLabel;
  count: number;
}

export interface PinnedReportSource {
  agent_id: string;
  session_id: string;
  run_id: string | null;
  message_id: string;
  store_alias: string;
  created_by: string;
}

export interface ColumnFormat {
  column: string;
  prefix: string;
  suffix: string;
}

export interface PinnedReportSection {
  name: string;
  description: string;
  query: string;
  query_keys: string[];
  data_table: string;
  default_view: Record<string, any>;
  column_formats?: ColumnFormat[];
}

export interface PinnedReport {
  pin_id: string;
  name: string;
  description: string;
  labels: PinLabel[];
  sections: PinnedReportSection[];
  source: PinnedReportSource;
  created_at: string;
  updated_at: string;
}

export interface PinnedReportListItem {
  pin_id: string;
  name: string;
  description: string;
  labels: PinLabel[];
  section_count: number;
  created_at: string;
  source: PinnedReportSource;
}

export interface PinnedReportListResponse {
  items: PinnedReportListItem[];
  total: number;
  limit: number;
  offset: number;
  labels: PinLabelFacet[];
}

export type RunPinSectionStatus = `success` | `error`;

export interface RunPinSection {
  name: string;
  status: RunPinSectionStatus;
  columns: string[];
  rows: Record<string, any>[];
  row_count: number;
  message: string | null;
  default_view: Record<string, any>;
  column_formats?: ColumnFormat[];
}

export interface UpdatePinSectionRequest {
  name?: string;
  description?: string;
  default_view?: Record<string, any>;
}

export interface UpdatePinRequest {
  name?: string;
  description?: string;
  labels?: PinLabel[] | null;
  sections?: (UpdatePinSectionRequest | null)[];
}

export interface RunPinResponse {
  pin_id: string;
  name: string;
  sections: RunPinSection[];
}

export interface SectionSortSpec {
  column: string;
  direction: `ascending` | `descending`;
}

export interface UpdateSectionViewRequest {
  type?: string;
  'x-axis'?: string;
  'y-axis'?: string;
  breakdown?: string | null;
  sort?: SectionSortSpec | null;
  column_order?: string[];
  column_widths?: Record<string, number>;
}

export interface UpdateSectionViewResponse {
  pin_id: string;
  section_name: string;
  default_view: Record<string, any>;
  updated_at: string;
}
